/**
 * Shared helpers for the Employer Account Owners transition (single -> up to 3).
 *
 * Employer docs/API rows expose `accountOwners: string[]` as the only owner field. The
 * legacy `accountOwner` scalar is no longer written or returned; it survives only on
 * documents nothing has written since, so these helpers still read either shape until the
 * backfill has run in every environment.
 *
 * Owner emails are ALWAYS returned normalized (trimmed + lowercased), mirroring the
 * backend contract in functions/src/employer/accountOwnersUtil.js: every write
 * normalizes the owner emails, while `users.email` may keep legacy mixed casing (and
 * Firebase Auth always reports lowercase). Comparing owner values on their raw casing
 * silently drops matches, so never do it: normalize both sides — owner dropdown option
 * values are built with `normalizeOwnerEmail` — and compare with `===` / `includes`.
 */

/**
 * Trims and lowercases an owner email.
 * @param {*} email
 * @returns {string|null} null when the value is not a non-empty string
 */
export function normalizeOwnerEmail(email) {
  if (typeof email !== 'string') return null
  const normalized = email.trim().toLowerCase()
  return normalized || null
}

/**
 * Returns the owners of an employer-like object, falling back to the legacy scalar
 * field. Always returns a deduped array of normalized, non-empty emails.
 * @param {object|null|undefined} docLike - employer doc data or API row
 * @returns {string[]}
 */
export function getAccountOwners(docLike) {
  if (!docLike) return []
  const raw = Array.isArray(docLike.accountOwners)
    ? docLike.accountOwners
    : [docLike.accountOwner]
  const owners = []
  raw.forEach(entry => {
    const email = normalizeOwnerEmail(entry)
    if (email && !owners.includes(email)) owners.push(email)
  })
  return owners
}

/**
 * Splits a CSV "Account Owner" cell into raw emails.
 * Accepts ';' or ',' separators and tolerates surrounding quotes around each
 * email (e.g. `"a@x.com", "b@y.com"`) that spreadsheets inject when a single
 * cell holds comma-separated quoted values.
 * @param {string} cell
 * @returns {string[]}
 */
export function splitOwnerCell(cell) {
  if (!cell) return []
  return String(cell)
    .split(/[;,]/)
    .map(e => e.trim().replace(/^["']+|["']+$/g, '').trim())
    .filter(Boolean)
}
